/* eslint-env browser */
// Google / Apple sign-in through the Cognito Hosted UI (auth code + PKCE).
// The page leaves the site for the Hosted UI and comes back to /oauth/callback,
// so what the flow needs to continue is kept in sessionStorage. Tokens never go
// there: the callback page keeps them in memory and moves on inside the app.
import request from 'superagent';
import store from './store';
import config from './config';

const PENDING_KEY = 'tavourSsoPending';
const RETURN_PATH_KEY = 'tavourSsoReturnPath';
const CALLBACK_PATH = '/oauth/callback';
const FALLBACK_URL = 'https://www.tavour.com';
const MERGED_PROVIDER_IDENTITY = 'MERGED_PROVIDER_IDENTITY';
const LEGACY_ACCOUNT = 'LEGACY_ACCOUNT';
const APPLE_RELAY_DOMAIN = '@privaterelay.appleid.com';

export const PROVIDERS = {
  google: 'Google',
  apple: 'SignInWithApple',
};

const PROVIDER_LABELS = {
  [PROVIDERS.google]: 'Google',
  [PROVIDERS.apple]: 'Apple',
};

export const FAILED_MESSAGE = 'We could not sign you in, please try again.';
export const SSO_HINT = 'If you joined Tavour in the app with Google or Apple, use Continue with Google or Continue with Apple above.';

// The outcome of the last sign-in, read once by takeResult() / takeError().
let result = null;

const redirectUri = () => `${window.location.origin}${CALLBACK_PATH}`;

const readJson = (key) => {
  try {
    return JSON.parse(window.sessionStorage.getItem(key));
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  window.sessionStorage.setItem(key, JSON.stringify(value));
};

const base64Url = bytes => btoa(String.fromCharCode.apply(null, new Uint8Array(bytes)))
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const randomString = () => base64Url(window.crypto.getRandomValues(new Uint8Array(32)));

const sha256 = text => window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));

const decodeJwt = (token) => {
  const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const bytes = Uint8Array.from(atob(payload), c => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
};

// The token endpoint is on the Cognito domain, not the API, so this does not use client.js.
const postToken = (params, cb) => {
  request.post(`https://${config.cognitoDomain}/oauth2/token`)
    .type('form')
    .send(Object.assign({ client_id: config.cognitoClientId }, params))
    .end(cb);
};

// Access tokens last an hour; renew them before that, so a member who waits
// on a later step does not get errors.
const REFRESH_MARGIN_SECONDS = 300;
let refreshTimer = null;
// Grows on every clearSession, so a refresh reply that arrives after a logout
// or an email login is dropped instead of bringing the old tokens back.
let sessionGeneration = 0;

const stopRefreshTimer = () => {
  if (refreshTimer) window.clearTimeout(refreshTimer);
  refreshTimer = null;
};

const saveTokens = (body) => {
  store.set('authToken', body.access_token);
  store.set('idToken', body.id_token);
  if (body.refresh_token) store.set('refreshToken', body.refresh_token);
  stopRefreshTimer();
  if (body.expires_in > REFRESH_MARGIN_SECONDS) {
    const waitMs = (body.expires_in - REFRESH_MARGIN_SECONDS) * 1000;
    // eslint-disable-next-line no-use-before-define
    refreshTimer = window.setTimeout(() => refresh(() => {}), waitMs);
  }
};

export const isEnabled = () => Boolean(config.cognitoDomain && config.cognitoClientId);

// Google blocks its sign-in inside the Facebook and Instagram in-app browsers.
export const isGoogleBlocked = () => /FBAN|FBAV|Instagram/i.test(window.navigator.userAgent);

// A friendly name for the email, as Apple "Hide My Email" addresses mean nothing to people.
export const displayEmail = email => (
  (email || '').endsWith(APPLE_RELAY_DOMAIN) ? 'your Apple ID (private email)' : email
);

// Forgets a Google / Apple sign-in, e.g. when the member logs in with email instead.
export function clearSession() {
  sessionGeneration += 1;
  stopRefreshTimer();
  store.set('idToken', null);
  store.set('refreshToken', null);
  store.set('ssoNewUser', null);
}

// Sends the browser to the Hosted UI. `retried` marks the one automatic retry
// after the pre-signup Lambda linked this login to an existing account.
export function start(provider, retried = false, previous = {}) {
  return Promise.resolve().then(() => {
    const verifier = randomString();
    const state = randomString();
    const returnPath = previous.returnPath || window.location.pathname;
    writeJson(PENDING_KEY, {
      provider,
      verifier,
      state,
      retried,
      returnPath,
      zipCode: previous.zipCode || store.get('zipCode') || null,
    });
    window.sessionStorage.setItem(RETURN_PATH_KEY, returnPath);
    return sha256(verifier).then((digest) => {
      const query = [
        ['response_type', 'code'],
        ['client_id', config.cognitoClientId],
        ['redirect_uri', redirectUri()],
        ['identity_provider', provider],
        ['scope', 'openid email profile'],
        ['state', state],
        ['code_challenge', base64Url(digest)],
        ['code_challenge_method', 'S256'],
      ].map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
      window.location.assign(`https://${config.cognitoDomain}/oauth2/authorize?${query}`);
    });
  });
}

const errorFor = (query, provider) => {
  const description = query.error_description || '';
  if (query.error === 'access_denied' && !description) return { cancelled: true };
  if (description.includes(LEGACY_ACCOUNT)) return { error: 'legacyAccount', provider };
  if (description.includes(MERGED_PROVIDER_IDENTITY)) return { error: 'merged', provider };
  return { error: 'failed', provider };
};

// Only a path on this site, never "//other.host", may be a place to go back to.
const isSitePath = path => typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

// Leaves the callback page for the club page the sign-in started from.
export function returnToClub() {
  const path = window.sessionStorage.getItem(RETURN_PATH_KEY);
  window.location.replace(isSitePath(path) ? path : FALLBACK_URL);
}

// Runs on /oauth/callback. Calls cb(returnPath) when the browser should go back
// to the brewery page; the outcome is read there with takeResult().
export function handleCallback(query, cb) {
  const pending = readJson(PENDING_KEY);
  window.sessionStorage.removeItem(PENDING_KEY);
  if (!pending || !isSitePath(pending.returnPath)) {
    // Back button or reload on the callback page.
    returnToClub();
    return;
  }
  const finish = (outcome) => {
    result = Object.assign({ zipCode: pending.zipCode }, outcome);
    cb(pending.returnPath);
  };

  if (query.state !== pending.state) {
    finish({ error: 'failed', provider: pending.provider });
    return;
  }

  if (query.error) {
    const outcome = errorFor(query, pending.provider);
    if (outcome.error === 'merged' && !pending.retried) {
      start(pending.provider, true, pending).catch(() => finish(outcome));
    } else {
      finish(outcome);
    }
    return;
  }

  if (!query.code) {
    finish({ error: 'failed', provider: pending.provider });
    return;
  }

  postToken({
    grant_type: 'authorization_code',
    code: query.code,
    redirect_uri: redirectUri(),
    code_verifier: pending.verifier,
  }, (err, res) => {
    if (err || !res.body.id_token) {
      finish({ error: 'failed', provider: pending.provider });
    } else {
      finish({ tokens: res.body });
    }
  });
}

// Reads (once) the outcome of a finished sign-in. With tokens, it loads them
// into the store and returns { zipCode, newUser } where newUser holds the
// token's name and email when the Google/Apple login has no Tavour account yet.
// An error stays for takeError(), which the Google/Apple buttons show.
export function takeResult() {
  const outcome = result;
  if (!outcome || !outcome.tokens) {
    if (outcome && outcome.cancelled) result = null;
    return outcome && { zipCode: outcome.zipCode, signedIn: false };
  }
  result = null;

  let claims;
  try {
    claims = decodeJwt(outcome.tokens.id_token);
  } catch (e) {
    result = { error: 'failed' };
    return { zipCode: outcome.zipCode, signedIn: false };
  }
  saveTokens(outcome.tokens);
  const newUser = claims['custom:tavour_id'] ? null : {
    email: claims.email,
    fname: claims.given_name || '',
    lname: claims.family_name || '',
  };
  return { zipCode: outcome.zipCode, signedIn: true, newUser };
}

const errorMessage = (outcome) => {
  const name = PROVIDER_LABELS[outcome.provider] || 'Google or Apple';
  switch (outcome.error) {
    case 'legacyAccount':
      return {
        text: 'You already have a Tavour account with this email from before Google and Apple sign-in. To use it here, set a password, then log in with your email and new password.',
        link: { href: `${config.accountHost}/forgot`, label: 'Set a password' },
      };
    case 'merged':
      return { text: `Your ${name} login is now connected to your Tavour account. Tap Continue with ${name} once more to sign in.` };
    default:
      return { text: FAILED_MESSAGE };
  }
};

// Reads (once) the error of the last sign-in as { text, link }, or null.
export function takeError() {
  if (!result || !result.error) return null;
  const message = errorMessage(result);
  result = null;
  return message;
}

// Gets new tokens, e.g. so the id token carries custom:tavour_id after signup.
export function refresh(cb) {
  const refreshToken = store.get('refreshToken');
  if (!refreshToken) {
    cb(new Error('No refresh token'));
    return;
  }
  const generation = sessionGeneration;
  postToken({ grant_type: 'refresh_token', refresh_token: refreshToken }, (err, res) => {
    if (generation !== sessionGeneration) {
      cb(new Error('Session ended'));
      return;
    }
    if (!err) saveTokens(res.body);
    cb(err);
  });
}



// WEBPACK FOOTER //
// ./src/lib/sso.js